/**
 * The per-patient safety settings (§7).
 *
 * These columns have existed since Phase 1 with sensible defaults and nothing
 * has ever been able to change them. This is that form.
 *
 * THEY ARE NOT PART OF THE PRESCRIPTION. Everything else on this screen is
 * draft work that takes effect when the doctor publishes; these take effect
 * immediately and survive every future version of the program. A ceiling that
 * waited for a publish would be a limit he believed was in force and was not.
 * That is why this panel saves itself rather than riding the draft autosave,
 * and why it says so on screen.
 *
 * NO ADVISORIES (Phase 3, 2026-08-22). Nothing here second-guesses a clinical
 * choice. The one message it can show is that a single exercise has been capped
 * longer than the whole session, which is not a judgment about dosing — it is a
 * setting the runner could never honour, because the session cap would stop it
 * first.
 *
 * WHAT IS DELIBERATELY ABSENT: `autoProgressionEnabled`. The column exists, but
 * nothing in this platform advances a dose on its own — progression is the
 * doctor editing the program — so a switch for it would be a control that does
 * nothing, which is worse than no control at all. It goes in when something
 * automatic exists for it to govern. See DECISIONS.md 2026-08-22.
 */

import type { SafetySettings } from '../../api.js';
import { Panel } from '../../components/Panel.js';
import styles from './SafetyPanel.module.css';

/**
 * Restated from the server's `safety/settings.ts`, which is the source. The
 * route clamps to the same numbers, so a value that gets past this form is
 * still brought inside them.
 */
const BOUNDS = {
  symptomAbsoluteCeiling: { min: 1, max: 10 },
  maxSessionMinutes: { min: 5, max: 60 },
  maxExerciseMinutes: { min: 1, max: 20 },
} as const;

const SENSITIVITY_CHOICES = [
  { value: 25, label: 'Less sensitive', hint: 'Stops later. Fewer false alarms.' },
  { value: 50, label: 'Usual', hint: 'The starting point for a new patient.' },
  { value: 75, label: 'More sensitive', hint: 'Stops sooner. Expect more stops.' },
] as const;

export type SafetySaveState = 'saved' | 'editing' | 'saving' | 'error';

export interface SafetyPanelProps {
  settings: SafetySettings;
  state: SafetySaveState;
  onChange: (next: SafetySettings) => void;
  onRetry: () => void;
}

export function SafetyPanel({ settings, state, onChange, onRetry }: SafetyPanelProps) {
  const set = (patch: Partial<SafetySettings>) => onChange({ ...settings, ...patch });
  const capTooLong = settings.maxExerciseMinutes > settings.maxSessionMinutes;

  return (
    <Panel
      title="Safety settings"
      summary={shutSummary(settings)}
      aside={<SaveState state={state} onRetry={onRetry} />}
    >
      <p className={styles.preamble}>
        These belong to the patient, not to the program. They apply straight
        away — there is nothing to publish — and they stay in force through every
        future version of their prescription.
      </p>

      <fieldset className={styles.group}>
        <legend className={styles.legend}>Stopping on symptoms</legend>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>End the session when they report</span>
            <select
              className={styles.select}
              value={settings.symptomDeltaTrigger}
              onChange={(e) => set({ symptomDeltaTrigger: Number(e.target.value) })}
            >
              <option value={1}>one step worse than they started</option>
              <option value={2}>two steps worse than they started</option>
            </select>
            <span className={styles.help}>
              Measured against their own answer at the start of today&rsquo;s
              session, not against a fixed level — someone who begins at &ldquo;a
              bit off&rdquo; and reaches &ldquo;not good&rdquo; has moved as far
              as someone who begins at &ldquo;good&rdquo; and reaches &ldquo;a
              bit off&rdquo;. <strong>&ldquo;Not good&rdquo; always ends the
              session</strong> whatever this is set to.
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Always stop at</span>
            <div className={styles.inlineRow}>
              <input
                className={styles.number}
                type="number"
                min={BOUNDS.symptomAbsoluteCeiling.min}
                max={BOUNDS.symptomAbsoluteCeiling.max}
                step={1}
                value={settings.symptomAbsoluteCeiling}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  if (Number.isFinite(raw)) set({ symptomAbsoluteCeiling: raw });
                }}
              />
              <span className={styles.unit}>out of 10</span>
            </div>
            <span className={styles.help}>
              Regardless of where they started. This is the 0&ndash;10 form,
              which opens only after they have already answered &ldquo;a bit
              off&rdquo; or &ldquo;not good&rdquo;, so it is a backstop rather
              than the usual route to a stop.
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.legend}>Time limits</legend>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Longest session</span>
            <div className={styles.inlineRow}>
              <input
                className={styles.number}
                type="number"
                min={BOUNDS.maxSessionMinutes.min}
                max={BOUNDS.maxSessionMinutes.max}
                step={1}
                value={settings.maxSessionMinutes}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  if (Number.isFinite(raw)) set({ maxSessionMinutes: raw });
                }}
              />
              <span className={styles.unit}>minutes</span>
            </div>
            <span className={styles.help}>
              Counted from the first exercise. A session that reaches this stops
              where it is and keeps everything done so far.
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Longest single exercise</span>
            <div className={styles.inlineRow}>
              <input
                className={styles.number}
                type="number"
                min={BOUNDS.maxExerciseMinutes.min}
                max={BOUNDS.maxExerciseMinutes.max}
                step={1}
                value={settings.maxExerciseMinutes}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  if (Number.isFinite(raw)) set({ maxExerciseMinutes: raw });
                }}
              />
              <span className={styles.unit}>minutes</span>
            </div>
            <span className={styles.help}>
              A backstop against one exercise being prescribed far longer than
              intended. It does not shorten a dose you meant.
            </span>
          </label>
        </div>

        {capTooLong && (
          <p className={styles.blocking} role="alert">
            One exercise is allowed longer than the whole session, so the session
            limit would always stop it first. Raise the session limit or lower
            the exercise one.
          </p>
        )}
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.legend}>Watching performance</legend>

        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={settings.decayMonitorEnabled}
            onChange={(e) => set({ decayMonitorEnabled: e.target.checked })}
          />
          <span>
            <span className={styles.toggleLabel}>
              Stop an exercise when performance drops
            </span>
            <span className={styles.help}>
              The monitor learns what this patient&rsquo;s first half-minute
              looks like, then watches for it to fall away — slower responses,
              more variability, poorer tracking. Performance drops before the
              patient notices symptoms, so this catches what asking them cannot.
              On for everyone unless there is a reason.
            </span>
          </span>
        </label>

        <fieldset className={styles.sensitivity} disabled={!settings.decayMonitorEnabled}>
          <legend className={styles.label}>How readily it stops</legend>
          <div className={styles.choices}>
            {SENSITIVITY_CHOICES.map((choice) => (
              <label key={choice.value} className={styles.choice}>
                <input
                  type="radio"
                  name="decaySensitivity"
                  value={choice.value}
                  checked={settings.decaySensitivity === choice.value}
                  onChange={() => set({ decaySensitivity: choice.value })}
                />
                <span>
                  <span className={styles.choiceLabel}>{choice.label}</span>
                  <span className={styles.choiceHint}>{choice.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className={styles.help}>
            Nobody knows the right setting for a given patient yet — it needs
            real sessions to tune against. If it stops someone too readily, the
            session it affected is on their chart with the reason attached, and
            the dose it reduced can be put back in one click.
          </p>
        </fieldset>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.legend}>Starting a new program</legend>

        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={settings.startSubThreshold}
            onChange={(e) => set({ startSubThreshold: e.target.checked })}
          />
          <span>
            <span className={styles.toggleLabel}>
              Run the first week below the prescribed dose
            </span>
            <span className={styles.help}>
              A new program starts short and comes up to the full dose after
              seven days. Under-dosing is the safer error at home, and volume
              can always be added at the next visit.
            </span>
          </span>
        </label>
      </fieldset>
    </Panel>
  );
}

/**
 * The settings in one line, for when the panel is shut.
 *
 * The monitor comes first and says so in words. It is the one setting whose
 * being off changes what the platform is for, and "off" has to be readable
 * without opening anything.
 */
function shutSummary(settings: SafetySettings): string {
  const monitor = settings.decayMonitorEnabled
    ? `Performance monitor on, ${sensitivityWord(settings.decaySensitivity)}`
    : 'Performance monitor OFF';
  const stop =
    settings.symptomDeltaTrigger === 1 ? 'stops on any step worse' : 'stops two steps worse';
  return `${monitor} · ${stop} · ${settings.maxSessionMinutes} min limit`;
}

function sensitivityWord(value: number): string {
  const match = SENSITIVITY_CHOICES.find((choice) => choice.value === value);
  return (match?.label ?? 'custom').toLowerCase();
}

function SaveState({ state, onRetry }: { state: SafetySaveState; onRetry: () => void }) {
  if (state === 'saving') return <span className={styles.saveState}>saving…</span>;
  if (state === 'editing') return <span className={styles.saveState}>editing…</span>;
  if (state === 'error') {
    return (
      <span className={styles.saveError} role="alert">
        not saved — the previous settings are still in force{' '}
        <button type="button" className={styles.inlineButton} onClick={onRetry}>
          Try again
        </button>
      </span>
    );
  }
  return <span className={styles.saveState}>in force</span>;
}
